import { gql } from "@apollo/client";
import { LazyMotion, domAnimation, m } from "framer-motion";
import Head from "next/head";
import { RefObject, useCallback, useRef, useState } from "react";
import { FaLanguage, FaTableTennis } from "react-icons/fa";
import { useAnalyticsTracker } from "../../../components/Atoms/Analytics";
import { CloudinaryImage } from "../../../components/Atoms/Cloudinary";
import ImageGallery from "../../../components/Atoms/ImageGallery";
import ModalScreen from "../../../components/Atoms/ModalScreen";
import TextCollapse from "../../../components/Atoms/TextCollapse";
import TourSignIcon from "../../../components/Atoms/TourSignIcon";
import ProductCarousel from "../../../components/Cards/ProductCarousel";
import GuideReview from "../../../components/Templates/Guide/GuideReview";
import ReviewStars from "../../../components/Templates/Guide/GuideReview/ReviewStars";
import {
  addApolloState,
  initializeApollo,
} from "../../../graphql/apolloClient";
import styles from "./GuideProfile.module.scss";

import { default as validateUuid } from "uuid-validate";
import GuideProfileVideo from "../../../components/Atoms/GuideProfileVideo";
import ProfileSchedule from "../../../components/Templates/Guide/GuideAvailability/ProfileSchedule";
import { Iso639 } from "../../../graphql/operations";
import Custom404 from "../../../pages/404";
import { CustomError } from "../../../utils/Error";

export default function GuideProfile({ result, shouldReload }) {
  const tracker = useAnalyticsTracker();

  if (shouldReload && process.browser && !!window) window.location.reload();
  const [videoVisible, setVideoVisible] = useState(false);
  const scheduleRef = useRef<HTMLDivElement>(null);
  const [shouldGalleryOpen, setGalleryOpen] = useState(false);
  const [viewingImage, setViewingImage] = useState({
    src: "",
    modalOpen: false,
  });

  const [state, setState] = useState({
    isAvailabilityOpen: false,
    reviewsIndex: 5,
    reviewsPage: 0,
    galleryPage: 0,
    galleryIndex: 6,
  });

  const scrollTo = useCallback((divRef: RefObject<HTMLDivElement>) => {
    if (divRef) {
      divRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  if (!result.data || !result.data.guide) return <Custom404 />;

  const { guide } = result.data;

  const numTours = guide.numTours;

  const hasReviews = guide.reviews.records && guide.reviews.records.length > 0;
  const guideProducts = guide.products.map((el) => el.product);
  const size = 290;

  const container = {
    hidden: { opacity: 1, scale: 0 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2,
      },
    },
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  return (
    <>
      <Head>
        <title>MagicalTrip Tour Guide: {guide.nickName}</title>
      </Head>

      <LazyMotion features={domAnimation}>
        <div className={styles.ProfileContainer}>
          <section className={styles["Hero"]}>
            <div className="AppLayout">
              <div className={styles["HeroContent"]}>
                {guide.profilePic && (
                  <div className={styles["HeroItem"]}>
                    <div className={styles["HeroItemContent"]}>
                      <m.div
                        initial={{ rotate: 0, opacity: 0, scale: 0 }}
                        animate={{ rotate: 100, scale: 1, opacity: 1 }}
                        // @ts-ignore
                        transition={{
                          easeIn: "linear",
                          type: "spring",
                          delay: 1,
                        }}
                        className={styles["HeroBox"]}
                      ></m.div>
                      <m.div
                        initial={{ rotate: 0, opacity: 0, scale: 0 }}
                        animate={{ rotate: 37, scale: 1, opacity: 1 }}
                        // @ts-ignore
                        transition={{
                          easeIn: "linear",
                          type: "spring",
                          delay: 1.5,
                        }}
                        className={styles["HeroBox"]}
                      ></m.div>
                      <m.div
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        // @ts-ignore
                        transition={{
                          easeIn: "linear",
                          type: "spring",
                          delay: 2,
                        }}
                        className={styles["HeroAvatarContainer"]}
                        onClick={() => {
                          if (guide.introVideo) {
                            setVideoVisible(true);
                            tracker.trackEvent({
                              category: "guide-profile",
                              action: `click-video-${guide.id}`,
                              label: "watch video",
                            });
                          }
                        }}
                      >
                        <CloudinaryImage
                          src={guide.profilePic}
                          transformations={[`w_${size}`, `h_${size}`, "c_fill"]}
                        />
                        {guide.introVideo ? (
                          <button
                            onClick={() => {
                              setVideoVisible(true);
                              tracker.trackEvent({
                                category: "guide-profile",
                                action: `click-video-${guide.id}`,
                                label: "watch video",
                              });
                            }}
                            className={styles["WatchVideoBtn"]}
                          >
                            Watch Video
                          </button>
                        ) : null}
                      </m.div>
                      {guide.introVideo ? (
                        <button
                          onClick={() => {
                            setVideoVisible(true);
                            tracker.trackEvent({
                              category: "guide-profile",
                              action: `click-video-${guide.id}`,
                              label: "watch video",
                            });
                          }}
                          className={styles["WatchVideoBtnMobile"]}
                        >
                          Watch Video
                        </button>
                      ) : null}
                    </div>
                  </div>
                )}
                <div className={styles["HeroItem"]}>
                  <div className={styles["GuideStars"]}>
                    <ReviewStars
                      rating={guide.reviews?.avgStars}
                      size={18}
                      style={{
                        marginTop: 20,
                      }}
                    />
                    <p>
                      <TourSignIcon
                        size={24}
                        style={{
                          position: "relative",
                          top: 4,
                          marginRight: 3,
                        }}
                      />{" "}
                      MagicalTrip Tour Guide
                    </p>
                  </div>
                  <h1>{guide.nickName}</h1>
                  <div className={styles["HeaderBorder"]} />

                  <div className={styles["GuideStarsMobile"]}>
                    <ReviewStars
                      rating={guide.reviews?.avgStars}
                      size={18}
                      style={{
                        marginTop: 20,
                      }}
                    />
                    <p>MagicalTrip Tour Guide</p>
                  </div>
                  {guide.translation?.description && (
                    <div
                      style={{
                        color: "#777778",
                      }}
                    >
                      <TextCollapse
                        maxlength={360}
                        text={guide.translation?.description}
                      />
                    </div>
                  )}
                  <div className={styles["GuideStats"]}>
                    <div className={styles["GuideStatItem"]}>
                      <span>{numTours}</span>
                      <span>Tours Guided</span>
                    </div>
                    <div className={styles["GuideStatItem"]}>
                      <span>{guide.numCustomers}</span>
                      <span>Customers</span>
                    </div>
                    <div className={styles["GuideStatItem"]}>
                      <span>{guide.reviews.totalRecords}</span>
                      <span>Reviews</span>
                    </div>
                  </div>

                  <button
                    onClick={() => scrollTo(scheduleRef)}
                    className={styles["ScheduleBtn"]}
                  >
                    View My Schedule
                  </button>
                </div>
              </div>
            </div>
          </section>

          {guide.media &&
          guide.media.length !== 0 &&
          guide.languages &&
          guide.languages.length !== 0 &&
          guide.translation?.hobbies &&
          guide.translation?.hobbies.length !== 0 ? (
            <section className={styles["GuideContent"]}>
              <div className={styles["GuideContentItem"]}>
                <div className="AppLayout">
                  {guide.languages && guide.languages.length > 0 && (
                    <>
                      <FaLanguage
                        size={100}
                        className={styles["LanguageIcon"]}
                        color="#EE394B"
                      />
                      <h3>Spoken Languages</h3>
                      <div className={styles["HeaderBorder"]} />
                      {guide.languages.map((l, i) => (
                        <span key={i}> {l.name}</span>
                      ))}
                    </>
                  )}

                  {guide.translation?.hobbies &&
                  guide.translation?.hobbies.length > 0 ? (
                    <>
                      <FaTableTennis
                        size={60}
                        className={styles["TennisIcon"]}
                        color="#EE394B"
                      />
                      <h3>My Hobbies</h3>
                      <div className={styles["HeaderBorder"]} />
                      {guide.translation.hobbies.map((h, index) => {
                        return <span key={index}>{h}</span>;
                      })}
                    </>
                  ) : null}
                </div>
              </div>

              <div className={styles["GuideContentItem"]}>
                <div className="AppLayout">
                  <h3 className={styles["GalleryTitle"]}>Gallery</h3>
                  <m.div
                    variants={container}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className={styles["GalleryImgContainer"]}
                  >
                    {guide.media && guide.media.length > 0
                      ? guide.media.slice(0, 6).map((m, index) => {
                          return (
                            <div
                              key={index}
                              className={styles["GalleryImg"]}
                              onClick={() =>
                                setViewingImage({
                                  modalOpen: true,
                                  src: m.url,
                                })
                              }
                            >
                              <CloudinaryImage
                                src={m.url}
                                transformations={[`w_200`, `h_200`, "c_fill"]}
                              />
                            </div>
                          );
                        })
                      : null}
                    <div className={styles["GalleryImgCol"]}>
                      <m.div
                        variants={item}
                        className={styles["GalleryImgSquare"]}
                        onClick={() =>
                          setViewingImage({
                            modalOpen: true,
                            src: guide.media[3]?.url,
                          })
                        }
                      >
                        {guide.media[3] ? (
                          <CloudinaryImage
                            src={guide.media[3].url}
                            transformations={[`w_300`, `h_270`, "c_fill"]}
                          />
                        ) : null}
                      </m.div>
                      <m.div
                        variants={item}
                        className={styles["GalleryImgSquare"]}
                        onClick={() =>
                          setViewingImage({
                            modalOpen: true,
                            src: guide.media[0]?.url,
                          })
                        }
                      >
                        {guide.media[0] ? (
                          <CloudinaryImage
                            src={guide.media[0].url}
                            transformations={[`w_300`, `h_270`, "c_fill"]}
                          />
                        ) : null}
                      </m.div>
                    </div>
                    <div className={styles["GalleryImgCol"]}>
                      <m.div
                        variants={item}
                        className={styles["GalleryImgSquare"]}
                        onClick={() =>
                          setViewingImage({
                            modalOpen: true,
                            src: guide.media[1]?.url,
                          })
                        }
                      >
                        {guide.media[1] ? (
                          <CloudinaryImage
                            src={guide.media[1].url}
                            transformations={[`w_300`, `h_270`, "c_fill"]}
                          />
                        ) : null}
                      </m.div>
                      <m.div
                        variants={item}
                        className={styles["GalleryImgSquare"]}
                        onClick={() =>
                          setViewingImage({
                            modalOpen: true,
                            src: guide.media[2]?.url,
                          })
                        }
                      >
                        {guide.media[2] ? (
                          <CloudinaryImage
                            src={guide.media[2].url}
                            transformations={[`w_300`, `h_270`, "c_fill"]}
                          />
                        ) : null}
                      </m.div>
                    </div>

                    <div className={styles["GalleryImgCol"]}>
                      <m.div
                        variants={item}
                        className={styles["GalleryImgSquare"]}
                        onClick={() =>
                          setViewingImage({
                            modalOpen: true,
                            src: guide.media[4]?.url,
                          })
                        }
                      >
                        {guide.media[4] ? (
                          <CloudinaryImage
                            src={guide.media[4].url}
                            transformations={[`w_300`, `h_270`, "c_fill"]}
                          />
                        ) : null}
                      </m.div>
                      {guide.media.length > 5 ? (
                        <m.button
                          variants={item}
                          className={styles["AltBtn"]}
                          onClick={() => setGalleryOpen(true)}
                        >
                          View Photos
                        </m.button>
                      ) : null}

                      <div style={{ display: "none" }}>
                        <ImageGallery
                          setGalleryOpen={setGalleryOpen}
                          openGallery={shouldGalleryOpen}
                          photos={guide.media}
                        />
                      </div>
                    </div>
                  </m.div>
                  {guide.media.length > 5 ? (
                    <button
                      className={styles["AltBtnPhoto"]}
                      onClick={() => setGalleryOpen(true)}
                    >
                      View Photos
                    </button>
                  ) : null}
                </div>
              </div>
            </section>
          ) : null}

          {hasReviews ? (
            <section className={styles["GuideReviews"]}>
              <img
                className={styles["GuideLogo"]}
                src="https://res.cloudinary.com/dbm1qiew0/image/upload/f_auto,q_auto,w_75,h_75,dpr_2.0/v1672881664/recruit/featured/mt-logo.jpg"
                alt="MagicalTrip Guide logo"
              />
              <div className="AppLayout">
                <h2>Reviews ({guide.reviews.totalRecords}) </h2>
                <div className={styles["HeaderBorder"]} />
              </div>
              <div
                className="AppLayout"
                style={{
                  textAlign: "center",
                }}
              >
                <div className={styles["GuideReviewsContainer"]}>
                  {guide.reviews.records
                    .slice(state.reviewsPage, state.reviewsIndex)
                    .map((review, index) => (
                      <GuideReview key={index} {...review} />
                    ))}
                </div>
                <br />
                {guide.reviews.totalRecords > 5 &&
                state.reviewsIndex < guide.reviews.totalRecords ? (
                  <button
                    onClick={() =>
                      setState({
                        ...state,
                        reviewsIndex: state.reviewsIndex + 5,
                      })
                    }
                    className={styles["AltBtn"]}
                  >
                    Load more
                  </button>
                ) : null}
              </div>
            </section>
          ) : null}

          <section ref={scheduleRef} className={styles["GuideSchedule"]}>
            <div className="AppLayout">
              <h2>My Upcoming Tours</h2>

              <div className={styles["HeaderBorder"]} />
            </div>
            <div className="AppLayout">
              <div className={styles["GuideScheduleContainer"]}>
                <ProfileSchedule guideId={guide.id} />
              </div>
            </div>
          </section>
          <section className={styles["GuideTours"]}>
            <div className="AppLayout">
              <h2>Tours I Guide</h2>

              <div className={styles["HeaderBorder"]} />
            </div>
            <div className="AppLayout">
              <div className={styles["GuideToursContainer"]}>
                <ProductCarousel
                  items={guideProducts}
                  linkRef="guide_profile"
                />
              </div>
            </div>
          </section>

          {videoVisible ? (
            <ModalScreen
              transparent
              openVideo={videoVisible}
              onClose={() => {
                setVideoVisible(false);
              }}
            >
              <GuideProfileVideo
                guideId={guide.id}
                publicId={guide.introVideo}
              />
            </ModalScreen>
          ) : null}

          {viewingImage.modalOpen && (
            <ModalScreen
              openVideo={viewingImage.modalOpen}
              onClose={() => {
                setViewingImage({ ...viewingImage, modalOpen: false });
              }}
            >
              <div className={styles["ViewImageModalContainer"]}>
                <div>
                  <CloudinaryImage
                    src={viewingImage.src}
                    transformations={["w_300", "c_fill"]}
                  />
                </div>
              </div>
            </ModalScreen>
          )}
        </div>
      </LazyMotion>
    </>
  );
}

const GET_GUIDE = gql`
  query GuideProfileQuery($id: ID!, $language: Iso639!, $currency: Currency!) {
    guide(id: $id) {
      id
      numCustomers
      translation(lang: $language) {
        description
        hobbies
      }
      nickName
      profilePic
      introVideo

      languages {
        name
      }
      media {
        type
        url
      }
      products {
        product {
          id
          poster
          duration
          translation(lang: $language) {
            name
          }
          price(currency: $currency) {
            amount
            currency
          }

          tags {
            id
            translation(lang: $language) {
              name
            }
          }
          reviews {
            totalRecords
            avgStars
          }
        }
      }

      reviews {
        avgStars
        totalRecords
        records {
          stars
          text
          date
          userName
          profileImageUrl
        }
      }
      numTours
    }
  }
`;

const revalidate = process.env.ENVIRONMENT == "production" ? 3600 : 60;
export const getStaticProps = async (context: {
  params: any;
  query: any;
  req: any;
}) => {
  try {
    if (!validateUuid(context.params.id)) {
      const error: CustomError = new Error(
        `${context.params.id} is not a valid UUID`
      );
      error.code = "INVALID_UUID";
      throw error;
    }
    const apolloClient = initializeApollo();

    const result = await apolloClient.query({
      query: GET_GUIDE,
      variables: {
        id: context.params.id,
        language: Iso639.En,
        currency: context.req?.cookies.currency || "USD",
      },
    });

    if (!result.data.guide) {
      return {
        notFound: true,
      };
    }

    return addApolloState(apolloClient, {
      props: {
        result,
        shouldReload: false,
      },
      revalidate,
    });
  } catch (error) {
    const result =
      error.code === "INVALID_UUID"
        ? {
            result: { data: { guide: null } },
            shouldReload: false,
          }
        : {
            result: { data: { guide: null } },
            shouldReload: true,
          };

    return {
      props: {
        result,
      },
      revalidate: 1,
    };
  }
};

const GET_ALL_GUIDES = gql`
  query GetAllGuides {
    guides {
      id
    }
  }
`;

export async function getStaticPaths() {
  try {
    const apolloClient = initializeApollo();
    const result = await apolloClient.query({
      query: GET_ALL_GUIDES,
    });

    // Get the paths we want to pre-render based on posts
    const paths = result.data.guides.map((guide: { id: any }) => ({
      params: { id: guide.id },
    }));

    // We'll pre-render only these paths at build time.
    // { fallback: blocking } will server-render pages
    // on-demand if the path doesn't exist.
    return { paths, fallback: "blocking" };
  } catch {
    return { paths: [], fallback: "blocking" };
  }
}
